/**
 * batch-69 samenstellen (FO-509 / FO-510 / FO-516) - de pure regels van de
 * samenstel-opdracht en de scan-afvinklijst.
 *
 * Eén plek voor de beslissingen die de API, de backoffice en de portal delen:
 *
 *  - de stand van een regel en van de hele opdracht (`bepaalSamenstelRegelStatus`,
 *    `bepaalSamenstellingStatus`), plus de kleur groen/oranje/rood per regel;
 *  - de compleet-regel (`toetsCompleetRegel`): een pakket gaat alles-of-niets de
 *    deur uit. Gereedschap, sets en het welkomstpakket alleen als elke regel is
 *    afgevinkt of met reden als uitzondering ("volgt na") is gemarkeerd; kleding
 *    mag deels (A2 in de spec);
 *  - de voortgang ("14 van 17 gescand");
 *  - wat een scan betekent (`bepaalScanUitkomst`): welke regel hij afvinkt, of
 *    waarom hij geweigerd wordt. Nooit stil: elke weigering heeft een code en
 *    een melding die de magazijnmedewerker begrijpt;
 *  - de diff bij een gewijzigd pakket (`pakketDiff`), zodat een herberekening
 *    nooit stil regels laat verschijnen of verdwijnen;
 *  - het risico ten opzichte van de startdatum (`bepaalSamenstelRisico`).
 *
 * Geen DB, geen datums uit `process.tz`: de service levert NL-dagen aan als
 * `yyyy-mm-dd`.
 */

export type SamenstelRegelDeel = 'GEREEDSCHAP' | 'KLEDING' | 'SET' | 'WELKOMST'

export type SamenstelRegelStatus =
  | 'GEBLOKKEERD'
  | 'WACHT_OP_GOEDKEURING'
  | 'TE_BESTELLEN'
  | 'BESTELD'
  | 'IN_MAGAZIJN'
  | 'GESCAND'
  | 'UITGEGEVEN'
  | 'VOLGT_NA'
  | 'VERVALLEN'

export type SamenstellingStatus = 'WACHT_OP_LEVERING' | 'KLAAR' | 'UITGEGEVEN' | 'GEANNULEERD'

export type SamenstelRegelKleur = 'GROEN' | 'ORANJE' | 'ROOD' | 'GRIJS'

// ─── Stand van een regel ──────────────────────────────────────────────────────

/** De feiten waaruit de stand van één regel volgt (door de service verzameld). */
export interface SamenstelRegelFeiten {
  /** Aantal stuks dat deze regel moet leveren. */
  aantal: number
  /** De regel valt weg na een pakketwijziging. */
  vervallen: boolean
  /** Kan niet samengesteld worden (maat ontbreekt of past niet, geen voorraadbron). */
  geblokkeerd: boolean
  /** Kleding: de pakketbestelling wacht nog op goedkeuring (credits). */
  wachtOpGoedkeuring: boolean
  /** Stuks met een actieve reservering in het magazijn. */
  aantalGereserveerd: number
  /** Stuks die besteld zijn bij een leverancier (verstuurde bestelling, extern deel). */
  aantalBesteld: number
  /** Stuks die op de afvinklijst zijn afgevinkt (gescand of met reden). */
  aantalGescand: number
  /** Stuks die al bij de medewerker (of in de koffer) zijn. */
  aantalUitgegeven: number
  /** Uitgegeven zonder deze regel: volgt na (uitzondering met reden). */
  uitzondering: boolean
}

/**
 * De stand van één regel. Volgorde van de toetsen is bewust: wat al uitgegeven
 * is wint van alles; een uitzondering (volgt na) wint van de voorraadstand; een
 * blokkade of een wachtende goedkeuring wint van "op voorraad" (zonder maat of
 * akkoord valt er niets af te vinken).
 */
export function bepaalSamenstelRegelStatus(r: SamenstelRegelFeiten): SamenstelRegelStatus {
  if (r.vervallen) return 'VERVALLEN'
  const open = Math.max(0, r.aantal - r.aantalUitgegeven)
  if (open === 0) return 'UITGEGEVEN'
  if (r.uitzondering) return 'VOLGT_NA'
  if (r.geblokkeerd) return 'GEBLOKKEERD'
  if (r.wachtOpGoedkeuring) return 'WACHT_OP_GOEDKEURING'
  if (r.aantalGescand >= open) return 'GESCAND'
  if (r.aantalGereserveerd >= open) return 'IN_MAGAZIJN'
  if (r.aantalGereserveerd + r.aantalBesteld >= open) return 'BESTELD'
  return 'TE_BESTELLEN'
}

/**
 * Kleur per regel (spec 209 §A2): groen = op voorraad (alles gereserveerd of al
 * afgevinkt), oranje = deels (een deel ligt er, de rest moet komen), rood =
 * bestellen of geblokkeerd. Grijs voor wat niet meer meetelt.
 */
export function samenstelRegelKleur(
  status: SamenstelRegelStatus,
  feiten: Pick<SamenstelRegelFeiten, 'aantalGereserveerd' | 'aantalUitgegeven'>,
): SamenstelRegelKleur {
  switch (status) {
    case 'VERVALLEN':
    case 'UITGEGEVEN':
      return 'GRIJS'
    case 'IN_MAGAZIJN':
    case 'GESCAND':
      return 'GROEN'
    case 'GEBLOKKEERD':
      return 'ROOD'
    case 'VOLGT_NA':
      return 'ORANJE'
    case 'WACHT_OP_GOEDKEURING':
    case 'TE_BESTELLEN':
    case 'BESTELD':
      return feiten.aantalGereserveerd > 0 || feiten.aantalUitgegeven > 0 ? 'ORANJE' : 'ROOD'
    default: {
      const _x: never = status
      return _x
    }
  }
}

/** Statussen waarin een regel "ligt in het magazijn" (of al weg is). */
const KLAAR_STATUSSEN: ReadonlySet<SamenstelRegelStatus> = new Set(['IN_MAGAZIJN', 'GESCAND', 'UITGEGEVEN'])

/**
 * De stand van de hele opdracht. Een geannuleerde of uitgegeven opdracht blijft
 * dat (de open regels van een uitgegeven opdracht volgen na en zijn per regel
 * zichtbaar). Anders: KLAAR zodra elke levende regel in het magazijn ligt.
 */
export function bepaalSamenstellingStatus(
  regels: ReadonlyArray<{ status: SamenstelRegelStatus }>,
  huidig: SamenstellingStatus,
): SamenstellingStatus {
  if (huidig === 'GEANNULEERD' || huidig === 'UITGEGEVEN') return huidig
  const levend = regels.filter((r) => r.status !== 'VERVALLEN')
  if (levend.length > 0 && levend.every((r) => KLAAR_STATUSSEN.has(r.status))) return 'KLAAR'
  return 'WACHT_OP_LEVERING'
}

// ─── Voortgang ────────────────────────────────────────────────────────────────

export interface SamenstelVoortgang {
  /** Regels die afgevinkt (of al uitgegeven) zijn. */
  gescand: number
  /** Levende regels (zonder vervallen). */
  totaal: number
  /** Stuks afgevinkt / stuks nodig, voor wie per stuk telt. */
  stuksGescand: number
  stuksTotaal: number
  /** "14 van 17 gescand". */
  label: string
}

export function samenstelVoortgang(
  regels: ReadonlyArray<{ status: SamenstelRegelStatus; aantal: number; aantalGescand: number; aantalUitgegeven: number }>,
): SamenstelVoortgang {
  const levend = regels.filter((r) => r.status !== 'VERVALLEN')
  const gescand = levend.filter((r) => r.status === 'GESCAND' || r.status === 'UITGEGEVEN').length
  const stuksTotaal = levend.reduce((s, r) => s + r.aantal, 0)
  const stuksGescand = levend.reduce(
    (s, r) => s + Math.min(r.aantal, r.aantalUitgegeven + r.aantalGescand),
    0,
  )
  return {
    gescand,
    totaal: levend.length,
    stuksGescand,
    stuksTotaal,
    label: `${gescand} van ${levend.length} gescand`,
  }
}

// ─── Compleet-regel ───────────────────────────────────────────────────────────

export interface CompleetRegelInvoer {
  id: string
  deel: SamenstelRegelDeel
  status: SamenstelRegelStatus
  omschrijving: string
  /**
   * Stuks die al zijn afgevinkt. Bij een regel die "volgt na" gaat het afgevinkte
   * deel nu mee (3 nodig, 2 lagen er: die 2 gaan uit, de derde volgt na).
   */
  aantalGescand?: number
  /**
   * Kleding: stuks die gereserveerd in het magazijn liggen (het voorraaddeel van
   * de verwerkte bestelling). Is dat hele deel afgevinkt, dan gaat het mee terwijl
   * het externe deel nog onderweg is.
   */
  aantalGereserveerd?: number
  /** FO-510: deels uitgeven toegestaan voor dit deel (pakket-uitgiftemodus DEELS_TOEGESTAAN). */
  deelsToegestaan?: boolean
}

export type CompleetBlokkade = 'NIET_AFGEVINKT' | 'TEKORT' | 'GEBLOKKEERD' | 'WACHT_OP_GOEDKEURING'

export interface CompleetToets {
  /** Er mag uitgegeven worden (alles wat moet, ligt klaar en is afgevinkt). */
  magUitgeven: boolean
  /** Regels die nu uitgegeven worden. */
  uitTeGeven: string[]
  /** Kleding (of een deels-toegestaan deel) die nog niet klaar is: blijft open, volgt. */
  blijftOpen: string[]
  /** Regels die de uitgifte tegenhouden (compleet-regel), met de reden. */
  ontbrekend: Array<{ id: string; omschrijving: string; reden: CompleetBlokkade }>
}

function blokkadeVan(status: SamenstelRegelStatus): CompleetBlokkade {
  switch (status) {
    case 'GEBLOKKEERD':
      return 'GEBLOKKEERD'
    case 'WACHT_OP_GOEDKEURING':
      return 'WACHT_OP_GOEDKEURING'
    case 'TE_BESTELLEN':
    case 'BESTELD':
      return 'TEKORT'
    default:
      return 'NIET_AFGEVINKT'
  }
}

/**
 * De compleet-regel (spec 209 §B3 + §E8, spec 210 §A1): gereedschap, een set en
 * het welkomstpakket gaan alleen de deur uit als ELKE levende regel is afgevinkt
 * of als uitzondering "volgt na" staat (met reden, door magazijnbeheer). Kleding
 * mag deels: wat afgevinkt is gaat mee, de rest blijft open en volgt.
 *
 * Een regel die al uitgegeven is telt niet mee. Is er niets uit te geven, dan is
 * uitgeven ook niet toegestaan (een lege uitgifte bestaat niet).
 */
export function toetsCompleetRegel(regels: ReadonlyArray<CompleetRegelInvoer>): CompleetToets {
  const uitTeGeven: string[] = []
  const blijftOpen: string[] = []
  const ontbrekend: CompleetToets['ontbrekend'] = []
  for (const r of regels) {
    if (r.status === 'VOLGT_NA') {
      // Het afgevinkte deel gaat mee; de rest volgt na. Kleding niet: een
      // kledingregel gaat als geheel via de bestelling (alle reserveringen tegelijk).
      if ((r.aantalGescand ?? 0) > 0 && r.deel !== 'KLEDING') uitTeGeven.push(r.id)
      continue
    }
    if (r.status === 'VERVALLEN' || r.status === 'UITGEGEVEN') continue
    if (r.status === 'GESCAND') {
      uitTeGeven.push(r.id)
      continue
    }
    const deelsMag = r.deel === 'KLEDING' || r.deelsToegestaan === true
    // Kleding: het hele voorraaddeel is afgevinkt (het externe deel volgt).
    const gescand = r.aantalGescand ?? 0
    if (r.deel === 'KLEDING' && gescand > 0 && gescand >= (r.aantalGereserveerd ?? Infinity)) {
      uitTeGeven.push(r.id)
      continue
    }
    if (deelsMag) blijftOpen.push(r.id)
    else ontbrekend.push({ id: r.id, omschrijving: r.omschrijving, reden: blokkadeVan(r.status) })
  }
  return {
    magUitgeven: ontbrekend.length === 0 && uitTeGeven.length > 0,
    uitTeGeven,
    blijftOpen,
    ontbrekend,
  }
}

/** Leesbare samenvatting van wat de compleet-regel tegenhoudt. */
export function compleetBlokkadeMelding(ontbrekend: CompleetToets['ontbrekend']): string {
  if (ontbrekend.length === 0) return ''
  const namen = ontbrekend.slice(0, 3).map((o) => o.omschrijving)
  const rest = ontbrekend.length > 3 ? ` en ${ontbrekend.length - 3} andere` : ''
  return (
    `Het pakket is niet compleet: ${namen.join(', ')}${rest} ${ontbrekend.length === 1 ? 'is' : 'zijn'} nog niet afgevinkt. ` +
    'Een pakket gaat alleen compleet de deur uit; magazijnbeheer kan een regel met reden als "volgt na" markeren.'
  )
}

// ─── Scan ─────────────────────────────────────────────────────────────────────

/** Een regel zoals de scanbeslissing hem nodig heeft. */
export interface ScanRegel {
  id: string
  deel: SamenstelRegelDeel
  status: SamenstelRegelStatus
  voorraadArtikelId: string | null
  /** Het catalogusartikel (UitrustingArtikel) van de maat-variant: herkent een verkeerde maat. */
  catalogusArtikelId: string | null
  omschrijving: string
  aantal: number
  aantalGescand: number
  aantalUitgegeven: number
  aantalGereserveerd: number
  gescandeItemIds: readonly string[]
  /** Exemplaren met een actieve reservering voor deze regel. */
  gereserveerdeItemIds: readonly string[]
  /** Exemplaren die voor deze regel defect zijn gemeld. */
  uitgeslotenItemIds: readonly string[]
  /** Er mag bij een scan direct uit de vrije voorraad gereserveerd worden (niet bij kleding: dat doet de verwerking). */
  magDirectReserveren: boolean
  /** Fase B (FO-510) - set-regel: het template waarvan een complete set gescand moet worden. */
  setPakketId?: string | null
}

/** Wat de service over de gescande code heeft vastgesteld. */
export type ScanFeit =
  | {
      soort: 'UNIEK'
      uniekItemId: string
      artikelId: string
      /** Catalogusartikel van het gescande artikel (voor "verkeerde maat"). */
      catalogusArtikelId: string | null
      status: string
      /** Huidige houder (bij UITGEGEVEN). */
      houderEmployeeId: string | null
      houderNaam: string | null
      /** De opdrachtregel waarvoor dit exemplaar gereserveerd is (deze opdracht), of null. */
      gereserveerdVoorRegelId: string | null
      /** Gereserveerd voor iets anders (een andere opdracht, aanvraag of bestelling). */
      gereserveerdElders: boolean
      /** Ligt in een koffer (IN_DRAGER): label van die koffer. */
      inDragerLabel: string | null
      /**
       * Fase B (FO-510): het gescande exemplaar is een koffer die als set op
       * voorraad ligt (template + set-stand); null = geen set.
       */
      set?: { pakketId: string | null; setStatus: 'IN_SAMENSTELLING' | 'COMPLEET' } | null
    }
  | {
      soort: 'BULK'
      artikelId: string
      catalogusArtikelId: string | null
      /** Vrije voorraad (saldo min reserveringen) over alle locaties. */
      vrij: number
    }

export type ScanFoutCode =
  | 'NIET_IN_LIJST'
  | 'VERKEERDE_MAAT'
  | 'AL_GESCAND'
  | 'AL_UITGEGEVEN'
  | 'BIJ_ANDERE_MEDEWERKER'
  | 'NIET_BESCHIKBAAR'
  | 'DEFECT_GEMELD'
  | 'GERESERVEERD_ELDERS'
  | 'IN_KOFFER'
  | 'REGEL_NIET_KLAAR'
  | 'GEEN_VOORRAAD'
  | 'SET_NIET_COMPLEET'

export type ScanUitkomst =
  | {
      ok: true
      regelId: string
      /**
       * AFVINKEN: de reservering ligt er al. WISSEL: een ander (vrij) exemplaar
       * van hetzelfde artikel neemt de plek in van een gereserveerd maar nog niet
       * gescand exemplaar. RESERVEER: er ligt nog geen reservering voor dit stuk
       * (bijvoorbeeld net binnengekomen), de scan reserveert en vinkt af.
       */
      actie: 'AFVINKEN' | 'WISSEL' | 'RESERVEER'
      aantal: number
    }
  | { ok: false; code: ScanFoutCode; melding: string }

/**
 * Scanbaar: wat in het magazijn ligt of nog moet komen, en (integrator-review M2)
 * een regel die als uitzondering "volgt na": zodra het artikel er is, vinkt een
 * scan hem af en gaat hij mee met de (na)levering.
 */
const OPEN_VOOR_SCAN: ReadonlySet<SamenstelRegelStatus> = new Set(['IN_MAGAZIJN', 'TE_BESTELLEN', 'BESTELD', 'VOLGT_NA'])

function fout(code: ScanFoutCode, melding: string): ScanUitkomst {
  return { ok: false, code, melding }
}

/** Stuks die op deze regel nog afgevinkt kunnen worden. */
function nogAfTeVinken(r: ScanRegel): number {
  return Math.max(0, r.aantal - r.aantalUitgegeven - r.aantalGescand)
}

/**
 * Wat betekent deze scan voor de opdracht? Nooit stil: een scan die niets
 * afvinkt geeft altijd een code met uitleg (spec 209 §E7: "duidelijke fout met
 * geluid/trilling, nooit stil").
 *
 * Unieke exemplaren worden EXACT afgevinkt (het gescande exemplaar gaat mee);
 * bulk telt per scan `aantal` op (default 1).
 */
export function bepaalScanUitkomst(
  regels: ReadonlyArray<ScanRegel>,
  feit: ScanFeit,
  opties: { aantal?: number; doelEmployeeId?: string | null } = {},
): ScanUitkomst {
  const levend = regels.filter((r) => r.status !== 'VERVALLEN')
  // Fase B (FO-510): een gescande koffer-set vinkt een set-regel van hetzelfde
  // template af; een los artikel nooit een set-regel (en andersom).
  const set = feit.soort === 'UNIEK' ? (feit.set ?? null) : null
  const vanArtikel = set
    ? levend.filter((r) => r.deel === 'SET' && r.setPakketId != null && r.setPakketId === set.pakketId)
    : levend.filter((r) => r.deel !== 'SET' && r.voorraadArtikelId === feit.artikelId)

  if (set && vanArtikel.length === 0) {
    return fout('NIET_IN_LIJST', 'Deze koffer hoort niet bij deze opdracht: er staat geen set van dit template op de lijst.')
  }
  if (vanArtikel.length === 0) {
    // Hetzelfde catalogusartikel in een andere maat staat wél op de lijst.
    const zelfdeArtikelAndereMaat =
      feit.catalogusArtikelId != null && levend.some((r) => r.catalogusArtikelId === feit.catalogusArtikelId)
    return zelfdeArtikelAndereMaat
      ? fout('VERKEERDE_MAAT', 'Verkeerde maat: dit artikel staat in een andere maat op de lijst. Pak de juiste maat.')
      : fout('NIET_IN_LIJST', 'Dit artikel hoort niet bij deze opdracht.')
  }

  if (feit.soort === 'UNIEK') {
    const alGescand = vanArtikel.find((r) => r.gescandeItemIds.includes(feit.uniekItemId))
    if (alGescand) return fout('AL_GESCAND', 'Dit exemplaar is al afgevinkt.')

    if (feit.status === 'UITGEGEVEN') {
      if (opties.doelEmployeeId && feit.houderEmployeeId === opties.doelEmployeeId) {
        return fout('AL_UITGEGEVEN', 'Dit exemplaar is al aan deze medewerker uitgegeven.')
      }
      return fout(
        'BIJ_ANDERE_MEDEWERKER',
        `Dit exemplaar is uitgegeven aan ${feit.houderNaam ?? 'een andere medewerker'}. Neem het niet over vanuit deze lijst.`,
      )
    }
    if (feit.status === 'IN_DRAGER') {
      return fout('IN_KOFFER', `Dit exemplaar ligt in ${feit.inDragerLabel ?? 'een koffer'}. Haal het daar eerst uit.`)
    }
    if (feit.status !== 'OP_VOORRAAD') {
      return fout('NIET_BESCHIKBAAR', 'Dit exemplaar is niet uitgeefbaar (defect, in reparatie, vermist of afgeschreven).')
    }
    if (vanArtikel.some((r) => r.uitgeslotenItemIds.includes(feit.uniekItemId))) {
      return fout('DEFECT_GEMELD', 'Dit exemplaar is bij deze opdracht als defect gemeld. Pak een ander exemplaar.')
    }
    if (set && set.setStatus !== 'COMPLEET') {
      return fout(
        'SET_NIET_COMPLEET',
        'Deze koffer is nog niet compleet (in samenstelling). Pak een complete set, of vul deze eerst aan via de afvinklijst.',
      )
    }

    // Gereserveerd voor een regel van deze opdracht: precies die regel.
    if (feit.gereserveerdVoorRegelId) {
      const regel = vanArtikel.find((r) => r.id === feit.gereserveerdVoorRegelId)
      if (regel && nogAfTeVinken(regel) > 0) return { ok: true, regelId: regel.id, actie: 'AFVINKEN', aantal: 1 }
    }
    if (feit.gereserveerdElders) {
      return fout('GERESERVEERD_ELDERS', 'Dit exemplaar is gereserveerd voor een andere uitgifte. Pak een ander exemplaar.')
    }

    const kandidaten = vanArtikel.filter((r) => nogAfTeVinken(r) > 0)
    if (kandidaten.length === 0) return fout('AL_GESCAND', 'Alles van dit artikel is al afgevinkt.')
    const nietKlaar = kandidaten.filter((r) => !OPEN_VOOR_SCAN.has(r.status))
    const scanbaar = kandidaten.filter((r) => OPEN_VOOR_SCAN.has(r.status))
    if (scanbaar.length === 0) {
      return fout('REGEL_NIET_KLAAR', regelNietKlaarMelding(nietKlaar[0]?.status ?? 'GEBLOKKEERD'))
    }
    // Eerst een regel met een gereserveerd-maar-ongescand exemplaar om te wisselen.
    const metWissel = scanbaar.find(
      (r) => r.gereserveerdeItemIds.some((id) => !r.gescandeItemIds.includes(id)),
    )
    if (metWissel) return { ok: true, regelId: metWissel.id, actie: 'WISSEL', aantal: 1 }
    const reserveerbaar = scanbaar.find((r) => r.magDirectReserveren)
    if (reserveerbaar) return { ok: true, regelId: reserveerbaar.id, actie: 'RESERVEER', aantal: 1 }
    return fout('REGEL_NIET_KLAAR', 'Dit deel is extern besteld en wordt niet uit het magazijn geleverd.')
  }

  // Bulk: tel per scan op.
  const gevraagd = Math.max(1, Math.floor(opties.aantal ?? 1))
  const kandidaten = vanArtikel.filter((r) => nogAfTeVinken(r) > 0)
  if (kandidaten.length === 0) return fout('AL_GESCAND', 'Alles van dit artikel is al afgevinkt.')
  const scanbaar = kandidaten.filter((r) => OPEN_VOOR_SCAN.has(r.status))
  if (scanbaar.length === 0) {
    return fout('REGEL_NIET_KLAAR', regelNietKlaarMelding(kandidaten[0]?.status ?? 'GEBLOKKEERD'))
  }
  // Eerst de regel met gereserveerde, nog niet afgevinkte stuks.
  const metReservering = scanbaar.find((r) => r.aantalGereserveerd - r.aantalGescand > 0)
  if (metReservering) {
    const ruimte = Math.min(nogAfTeVinken(metReservering), metReservering.aantalGereserveerd - metReservering.aantalGescand)
    if (gevraagd > ruimte) {
      return fout(
        'AL_GESCAND',
        `Er ${ruimte === 1 ? 'staat' : 'staan'} nog ${ruimte} stuk(s) open op deze regel; je scande er ${gevraagd}.`,
      )
    }
    return { ok: true, regelId: metReservering.id, actie: 'AFVINKEN', aantal: gevraagd }
  }
  const reserveerbaar = scanbaar.find((r) => r.magDirectReserveren)
  if (!reserveerbaar) return fout('REGEL_NIET_KLAAR', 'Dit deel is extern besteld en wordt niet uit het magazijn geleverd.')
  if (feit.vrij < gevraagd) {
    return fout('GEEN_VOORRAAD', 'Er is geen vrije voorraad meer van dit artikel: het staat op de bestellijst.')
  }
  const ruimte = nogAfTeVinken(reserveerbaar)
  if (gevraagd > ruimte) {
    return fout('AL_GESCAND', `Er staan nog ${ruimte} stuk(s) open op deze regel; je scande er ${gevraagd}.`)
  }
  return { ok: true, regelId: reserveerbaar.id, actie: 'RESERVEER', aantal: gevraagd }
}

function regelNietKlaarMelding(status: SamenstelRegelStatus): string {
  switch (status) {
    case 'GEBLOKKEERD':
      return 'Deze regel is geblokkeerd (maat ontbreekt of past niet). Vul eerst de maat in.'
    case 'WACHT_OP_GOEDKEURING':
      return 'De kledingbestelling wacht nog op goedkeuring; afvinken kan daarna.'
    case 'VOLGT_NA':
      return 'Deze regel volgt na (uitzondering). Gebruik "Naleveren" als het artikel er is.'
    default:
      return 'Deze regel kan nu niet afgevinkt worden.'
  }
}

// ─── Pakketdiff ───────────────────────────────────────────────────────────────

export interface PakketRegelSnap {
  /** Sleutel waarop vergeleken wordt (het voorraadartikel, of de variant bij kleding). */
  sleutel: string
  omschrijving: string
  aantal: number
}

export interface PakketDiff {
  toegevoegd: PakketRegelSnap[]
  verwijderd: PakketRegelSnap[]
  gewijzigd: Array<{ sleutel: string; omschrijving: string; van: number; naar: number }>
}

/** Twee regels voor hetzelfde artikel tellen als één regel (aantallen opgeteld). */
function perSleutel(regels: ReadonlyArray<PakketRegelSnap>): Map<string, PakketRegelSnap> {
  const m = new Map<string, PakketRegelSnap>()
  for (const r of regels) {
    const b = m.get(r.sleutel)
    m.set(r.sleutel, b ? { ...b, aantal: b.aantal + r.aantal } : { ...r })
  }
  return m
}

/** Wat er is veranderd tussen de opdracht en het (gewijzigde) pakket. */
export function pakketDiff(oud: ReadonlyArray<PakketRegelSnap>, nieuw: ReadonlyArray<PakketRegelSnap>): PakketDiff {
  const o = perSleutel(oud)
  const n = perSleutel(nieuw)
  const toegevoegd: PakketRegelSnap[] = []
  const verwijderd: PakketRegelSnap[] = []
  const gewijzigd: PakketDiff['gewijzigd'] = []
  for (const [k, r] of n) {
    const was = o.get(k)
    if (!was) toegevoegd.push(r)
    else if (was.aantal !== r.aantal) gewijzigd.push({ sleutel: k, omschrijving: r.omschrijving, van: was.aantal, naar: r.aantal })
  }
  for (const [k, r] of o) if (!n.has(k)) verwijderd.push(r)
  const naam = (a: { omschrijving: string }, b: { omschrijving: string }) => a.omschrijving.localeCompare(b.omschrijving, 'nl')
  return { toegevoegd: toegevoegd.sort(naam), verwijderd: verwijderd.sort(naam), gewijzigd: gewijzigd.sort(naam) }
}

export function pakketDiffIsLeeg(d: PakketDiff): boolean {
  return d.toegevoegd.length === 0 && d.verwijderd.length === 0 && d.gewijzigd.length === 0
}

/** "2 regels toegevoegd, 1 verwijderd, 1 aantal gewijzigd" (leeg = geen wijziging). */
export function pakketDiffSamenvatting(d: PakketDiff): string {
  const delen: string[] = []
  if (d.toegevoegd.length) delen.push(`${d.toegevoegd.length} ${d.toegevoegd.length === 1 ? 'regel' : 'regels'} toegevoegd`)
  if (d.verwijderd.length) delen.push(`${d.verwijderd.length} verwijderd`)
  if (d.gewijzigd.length) delen.push(`${d.gewijzigd.length} ${d.gewijzigd.length === 1 ? 'aantal' : 'aantallen'} gewijzigd`)
  return delen.join(', ')
}

/** Stabiele vingerafdruk van een pakketinhoud (volgorde-onafhankelijk). */
export function pakketVersieHash(regels: ReadonlyArray<PakketRegelSnap>): string {
  return [...perSleutel(regels).values()]
    .map((r) => `${r.sleutel}:${r.aantal}`)
    .sort()
    .join('|')
}

// ─── Risico t.o.v. de startdatum ─────────────────────────────────────────────

export type SamenstelRisico = 'GEEN' | 'RISICO' | 'TE_LAAT'

/**
 * Risico dat de uitrusting niet compleet is voor de start (spec 209 §F10).
 * Datums als NL-dag `yyyy-mm-dd` (lexicografisch vergelijkbaar).
 *
 *  - Klaar, uitgegeven of geannuleerd: geen risico.
 *  - Gestart en nog niet compleet: TE_LAAT.
 *  - Iets nog niet besteld (of geblokkeerd/wacht op goedkeuring) en de start valt
 *    binnen `drempelDagen`: RISICO.
 *  - Verwachte compleetdatum na de start: RISICO.
 */
export function bepaalSamenstelRisico(input: {
  status: SamenstellingStatus
  startdatum: string | null
  verwachteCompleetOp: string | null
  heeftOnbesteld: boolean
  vandaag: string
  /** Dagen voor de start waarbinnen "nog niet besteld" een risico is. */
  dagenTotStart: number | null
  drempelDagen?: number
}): SamenstelRisico {
  if (input.status !== 'WACHT_OP_LEVERING') return 'GEEN'
  if (!input.startdatum) return 'GEEN'
  if (input.startdatum <= input.vandaag) return 'TE_LAAT'
  const drempel = input.drempelDagen ?? 10
  if (input.heeftOnbesteld && input.dagenTotStart != null && input.dagenTotStart <= drempel) return 'RISICO'
  if (input.verwachteCompleetOp && input.verwachteCompleetOp > input.startdatum) return 'RISICO'
  return 'GEEN'
}
